import { Link } from "react-router-dom";
import { FadeUp } from "@/components/FadeUp";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { VALUE_PROPS, SERVICES, INDUSTRIES, COMPANY, TRUST_BADGES, MIM_PHOTOS } from "@/data/site";

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section data-testid="home-hero" className="relative overflow-hidden">
        <div className="container-x pt-12 md:pt-20 pb-20 md:pb-28 grid lg:grid-cols-12 gap-10 lg:gap-16 items-end">
          <div className="lg:col-span-7">
            <p className="eyebrow" data-testid="hero-eyebrow">Est. Bayswater · Western Australia</p>
            <h1
              data-testid="hero-heading"
              className="mt-6 font-serif text-5xl sm:text-6xl md:text-7xl lg:text-[88px] leading-[0.95] tracking-tight text-primary"
            >
              Precision Machining.<br />
              <span className="italic text-secondary">Quality</span> Fabrication.<br />
              End-to-End Engineering.
            </h1>
            <p className="mt-8 text-lg md:text-xl text-muted-foreground max-w-xl leading-relaxed">
              A local engineering workshop trusted by industry to keep critical equipment running —
              from custom one-off parts to full breakdown response.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to="/quote"
                data-testid="hero-quote-cta"
                className="inline-flex items-center gap-2 px-6 py-3.5 bg-primary text-primary-foreground uppercase tracking-[0.18em] text-xs hover:bg-accent transition-colors"
              >
                Request a Quote <ArrowUpRight size={16} />
              </Link>
              <Link to="/services" data-testid="hero-secondary-cta" className="text-sm tracking-wider uppercase text-primary link-underline">
                Explore Capabilities →
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative aspect-[4/5] overflow-hidden image-hover">
              <img
                src={MIM_PHOTOS.hero}
                alt="MIM Engineering precision shaft assembly on the workshop floor"
                className="w-full h-full object-cover"
              />
              <div className="absolute left-0 bottom-0 bg-[hsl(var(--background))] px-5 py-4 border-t border-r border-border">
                <p className="eyebrow">No. 01 / Workshop</p>
                <p className="font-serif text-xl text-primary mt-1">Live Project — Shaft Assembly</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* VALUE PROPS */}
      <section data-testid="home-value-props" className="border-y border-border bg-white">
        <div className="container-x grid md:grid-cols-3">
          {VALUE_PROPS.map((v, i) => (
            <FadeUp
              key={v.title}
              delay={i * 0.08}
              className={`py-14 md:py-20 px-2 md:px-10 ${i < 2 ? "md:border-r border-border" : ""}`}
            >
              <span className="editorial-number text-3xl text-accent">0{i + 1}</span>
              <h3 className="font-serif text-3xl md:text-4xl text-primary mt-4">{v.title}</h3>
              <p className="mt-4 text-muted-foreground leading-relaxed">{v.desc}</p>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* SERVICES OVERVIEW — numbered editorial */}
      <section className="section-y">
        <div className="container-x grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-4">
            <p className="eyebrow">What We Do</p>
            <h2 className="font-serif text-5xl md:text-6xl text-primary mt-4 leading-none">Six pillars, one workshop.</h2>
            <p className="text-muted-foreground mt-6 text-lg leading-relaxed">
              From precision turning to powder-coated finished assemblies, MIM Engineering handles the full chain.
            </p>
            <Link to="/services" className="mt-8 inline-flex items-center gap-2 text-sm uppercase tracking-wider text-primary link-underline">
              All services <ArrowRight size={14} />
            </Link>
          </div>
          <div className="lg:col-span-8 divide-y divide-border border-t border-border">
            {SERVICES.map((s, i) => (
              <FadeUp key={s.id} delay={i * 0.05}>
                <Link
                  to={`/services#${s.id}`}
                  data-testid="service-row"
                  className="group grid grid-cols-12 gap-4 py-7 items-center hover:bg-white transition-colors"
                >
                  <span className="col-span-2 editorial-number text-2xl text-muted-foreground">{s.number}</span>
                  <span className="col-span-7 md:col-span-7 font-serif text-2xl md:text-3xl text-primary">{s.title}</span>
                  <span className="col-span-3 hidden md:block text-sm text-muted-foreground">{s.short}</span>
                  <ArrowUpRight className="col-span-12 md:col-span-0 text-primary opacity-0 group-hover:opacity-100 transition-opacity hidden md:block" />
                </Link>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* INDUSTRIES strip */}
      <section className="bg-muted/40 border-y border-border">
        <div className="container-x section-y">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <p className="eyebrow">Industries We Support</p>
              <h2 className="font-serif text-4xl md:text-5xl text-primary mt-3">Heavy industry, served end-to-end.</h2>
            </div>
            <Link to="/industries" className="text-sm uppercase tracking-wider link-underline">View all →</Link>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {INDUSTRIES.slice(0, 6).map((ind, i) => (
              <FadeUp key={ind.id} delay={i * 0.05}>
                <Link to="/industries" data-testid="industry-card" className="group block relative aspect-[5/6] overflow-hidden image-hover">
                  <img src={ind.image} alt={ind.title} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-primary/95 via-primary/40 to-transparent" />
                  <div className="absolute inset-0 p-6 flex flex-col justify-end text-white">
                    <span className="eyebrow text-white/70">0{i + 1}</span>
                    <h3 className="font-serif text-3xl mt-2 leading-tight">{ind.title}</h3>
                  </div>
                </Link>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* TRUSTED BY strip */}
      <section className="bg-white border-y border-border">
        <div className="container-x py-10 md:py-14">
          <p className="eyebrow text-center">Trusted across heavy industry</p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-10 gap-y-4 text-primary/80">
            {TRUST_BADGES.map((b) => (
              <span key={b} className="font-serif italic text-xl md:text-2xl tracking-tight">{b}</span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-y">
        <div className="container-x grid md:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="font-serif text-5xl md:text-6xl text-primary leading-none">
              Got a job?<br />
              <span className="italic text-secondary">Let&apos;s get it running.</span>
            </h2>
          </div>
          <div className="md:justify-self-end">
            <p className="text-lg text-muted-foreground max-w-md">
              Call us for urgent breakdowns or send a brief — we&apos;ll get a quote back fast.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <Link to="/quote" className="inline-flex items-center gap-2 px-6 py-3.5 bg-primary text-primary-foreground uppercase tracking-[0.18em] text-xs hover:bg-accent transition-colors">
                Request a Quote <ArrowUpRight size={16} />
              </Link>
              <a href={`tel:${COMPANY.phone.replace(/\s/g, '')}`} className="inline-flex items-center gap-2 px-6 py-3.5 border border-primary text-primary uppercase tracking-[0.18em] text-xs hover:bg-primary hover:text-primary-foreground transition-colors">
                Call {COMPANY.phone}
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
